import { ModifiedAnswerOption } from '@/client/quizzes';
import { RichTextEditor, RichTextEditorHandle } from '@/ui/rich-text';
import { DragHandleIcon, TrashIcon } from '@/utils/helpers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox } from 'primereact/checkbox';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const RowContainer = styled.div<{ $isDragging: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};

  .drag-handle {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    color: var(--gray-darker);
    opacity: 0;
    transition: opacity 0.2s ease;

    &:active {
      cursor: grabbing;
    }
  }

  &:hover .drag-handle {
    opacity: 1;
  }

  .p-checkbox {
    flex-shrink: 0;
    width: 23px;
    height: 23px;

    .p-checkbox-box {
      width: 23px;
      height: 23px;
      border-radius: var(--xxxsmall-border-radius);
    }

    &.p-highlight .p-checkbox-box {
      background: var(--red-main);
      border-color: var(--red-main);
    }
  }

  .rich-text-editor-root {
    flex: 1;
    min-height: unset;
    height: auto;
    background: transparent;
    border: none;
    border-radius: 0;

    &:hover {
      border: none;
    }

    &.p-editor-focused {
      border: none;
      box-shadow: none;
    }

    .ql-container {
      padding: 0;
      border: none;
      font-size: var(--small-font-size);
      font-family: var(--font-family);
    }

    .ql-blank::before {
      left: 0;
    }

    .ql-editor {
      padding: var(--xxsmall-padding) 0;
      min-height: unset;
    }
  }

  .delete-button {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--gray-darker);
    background: none;
    border: none;
    padding: 0;

    &:hover {
      color: var(--red-main);
    }
  }
`;

interface QuizAnswerRowProps {
  id: string;
  answer: ModifiedAnswerOption;
  answerIndex: number;
  isCorrect: boolean;
  translationKey: string;
  onToggleCorrect: (answerIndex: number) => void;
  onChangeText: (answerIndex: number, value: string) => void;
  onDelete: (answerIndex: number) => void;
  registerEditor: (handle: RichTextEditorHandle | null) => void;
  unregisterEditor: () => void;
  autoFocus?: boolean;
  onAutoFocusHandled?: () => void;
  isDragging?: boolean;
}

export const QuizAnswerRow: React.FC<QuizAnswerRowProps> = ({
  id,
  answer,
  answerIndex,
  isCorrect,
  translationKey,
  onToggleCorrect,
  onChangeText,
  onDelete,
  registerEditor,
  unregisterEditor,
  autoFocus,
  onAutoFocusHandled,
  isDragging: isDraggingProp,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<RichTextEditorHandle>(null);

  useEffect(() => {
    if (!autoFocus) return;

    editorRef.current?.focus();
    onAutoFocusHandled?.();
  }, [autoFocus, onAutoFocusHandled]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragging = isDraggingProp || sortableDragging;
  const answerText = answer.translations[translationKey]?.text ?? '';

  const handleFocus = () => {
    registerEditor(editorRef.current);
  };

  return (
    <RowContainer ref={setNodeRef} style={style} $isDragging={dragging}>
      <div className="drag-handle" {...attributes} {...listeners}>
        <DragHandleIcon />
      </div>
      <Checkbox
        checked={isCorrect}
        onChange={() => onToggleCorrect(answerIndex)}
      />
      <RichTextEditor
        key={`${answer.id ?? answer.clientKey ?? id}-${translationKey}`}
        ref={editorRef}
        defaultValue={answerText}
        placeholder={t('content.answer.option.placeholder')}
        onChange={(value) => onChangeText(answerIndex, value)}
        onFocus={handleFocus}
        onBlur={unregisterEditor}
      />
      <button
        type="button"
        className="delete-button"
        onClick={() => onDelete(answerIndex)}
      >
        <TrashIcon />
      </button>
    </RowContainer>
  );
};
